import * as React from 'react'
// antd's OWN DisabledContext, not a transcribed copy: it is what a
// `<Form disabled>` / `<ConfigProvider componentDisabled>` cascade writes, it
// is still real on this branch, and reading it here keeps a shadcn-mode
// Checkbox obeying exactly what an antd-mode one obeys.
import DisabledContext from 'antd/es/config-provider/DisabledContext'
import type { CheckboxRef } from 'rc-checkbox'

import { Checkbox as CheckboxPrimitive } from 'src/components/ui/checkbox'
import { cn } from 'src/lib/utils'

import type {
  CheckboxChangeEvent,
  CheckboxGroupProps,
  CheckboxOptionType,
  CheckboxProps,
  CheckboxValueType,
} from './antd-props'
import { useStableId } from './useStableId'

/* -------------------------------------------------------------------------
 * The SHADCN half of the `src/antd/Checkbox` seam. `./Checkbox.tsx` is the
 * dispatcher that chooses between this and antd's own Checkbox; nothing else
 * imports this file, and nothing reaches it until someone flips the design
 * toggle (`src/design-mode`).
 *
 * Started from the `update-design` reference branch's `src/antd/Checkbox.tsx`.
 * Divergences from the reference are marked `[development]`; everything else
 * follows it.
 *
 * The EXTERNAL API is antd's own `CheckboxProps` / `CheckboxGroupProps` and
 * antd's `CheckboxRef`, so the 81 files that import `Checkbox` from `src/antd`
 * switch with no edit — including every `Form.Item valuePropName="checked"`
 * child, which rc-field-form clones with `checked` / `onChange` / `id`.
 *
 * [development] The reference TRANSCRIBES antd's prop types and reads a
 * transcribed `DisabledContext`, because antd is deleted over there. Here both
 * are real, so this file takes the types from `./antd-props` (which serves
 * antd's own on this branch and the transcription on the reference one) and
 * the context from the package.
 *
 * [development] `.ant-checkbox*` IS NOT RE-EMITTED. antd is installed and the
 * toggle is a RUNTIME flip, so antd's checkbox stylesheet is on the page from
 * the moment anything rendered a checkbox in antd mode — unlayered, and
 * carrying the box geometry, the border, the tick and the label spacing this
 * seam replaces. That is the `.ant-btn` situation, not the `.ant-space` one.
 * The parts are keyed by `data-slot` only, and the seven live stylesheets that
 * reached into antd's names each grew a `[data-slot='…']` selector beside the
 * `.ant-checkbox-…` one.
 *
 * THE WRAPPER IS LOAD-BEARING. antd always wraps a Checkbox in a `<label>`,
 * with or without children, and this repo's CSS and event delegation target
 * that wrapper rather than the control: two list tables ask
 * `closest('… .ant-checkbox-wrapper …')` before treating a click as a row
 * activation, and `ReassignPopover` spaces siblings with
 * `.ant-checkbox-wrapper + .ant-checkbox-wrapper`. So the wrapper stays, as
 * `data-slot="checkbox-wrapper"`, and those call sites name both.
 *
 * [development] TWO REFERENCE BEHAVIOURS ARE CORRECTED HERE, both found by
 * reading antd 5.15.4's own source (`node_modules/antd/es/checkbox/`) rather
 * than the reference's markup:
 *
 *   • THE NEXT CHECKED STATE IS COMPUTED, not taken from Radix. Radix reports
 *     `true` for any click that leaves the `indeterminate` state, but antd
 *     toggles the REAL `checked` value — so an indeterminate checkbox that is
 *     also `checked` reports `false` in antd and would have reported `true`
 *     through the reference. `indeterminate` is a display state in antd, not
 *     a third value, and this keeps it one.
 *   • THE GROUP'S `onChange` ARGUMENT IS ORDERED AND FILTERED exactly as
 *     antd's is: the next value is filtered to the values still REGISTERED by
 *     a mounted child and sorted into `options` order. A group that emitted
 *     click order instead would reorder a persisted list on every edit.
 *
 * [development] THE CHANGE EVENT'S `nativeEvent` IS SYNTHESIZED, and that is
 * a divergence rather than a correction. Radix hands this seam only the next
 * checked value through `onCheckedChange`, never the DOM event, so
 * `buildChangeEvent` below attaches a DETACHED `new MouseEvent('click')`.
 * `e.stopPropagation()` / `e.preventDefault()` called from an `onChange`
 * handler therefore reach nothing here, where antd forwards them to the real
 * event. No call site does either today — the row-delegation sites act on
 * `onClick`, which still carries React's own event — so it is latent, and it
 * is named here rather than left to be rediscovered. `./Radio.shadcn.tsx` has
 * the same construction, for the same reason.
 *
 * THE LABEL IS PAIRED BOTH WAYS: the control is nested inside the `<label>`
 * AND named by `htmlFor`. The explicit pairing is what keeps
 * `jsx-a11y/label-has-associated-control` satisfied over a control that
 * renders as a `<button>` behind a custom component (see `./useStableId`).
 * `for` takes PRECEDENCE over nesting, so a CALLER-SUPPLIED duplicate `id`
 * — two unnamed antd `Form`s deriving the same field id, say — would point
 * the label at the first match on the page. The seam's OWN generated ids
 * cannot collide: `useStableId` is a process-wide counter. antd's own
 * `Form.Item` label carries the identical exposure for a caller-supplied
 * duplicate id, so this is not a failure mode the seam introduces.
 * ------------------------------------------------------------------------- */

interface CheckboxGroupContextValue {
  value: CheckboxValueType[]
  disabled?: boolean
  name?: string
  toggleOption: (option: CheckboxOptionType) => void
  registerValue: (val: CheckboxValueType) => void
  cancelValue: (val: CheckboxValueType) => void
}

const CheckboxGroupContext =
  React.createContext<CheckboxGroupContextValue | null>(null)

const buildChangeEvent = (
  props: CheckboxProps,
  checked: boolean,
  nativeEvent: MouseEvent
): CheckboxChangeEvent => ({
  target: { ...props, checked },
  stopPropagation: () => nativeEvent.stopPropagation(),
  preventDefault: () => nativeEvent.preventDefault(),
  nativeEvent,
})

// forwardRef: React 18 runtime (no ref-as-prop). The handle is antd's
// `CheckboxRef` shape rather than the bare button the reference exposes, so a
// caller that reaches for `.focus()` — or rc-field-form scrolling to the first
// invalid field — finds what it finds on the antd path.
const CheckboxBase = React.forwardRef<CheckboxRef, CheckboxProps>(
  (
    {
      className,
      style,
      defaultChecked,
      checked: checkedProp,
      disabled: disabledProp,
      indeterminate = false,
      onChange,
      onClick,
      onMouseEnter,
      onMouseLeave,
      onKeyDown,
      value,
      children,
      id,
      autoFocus,
      name,
      required,
      tabIndex,
      title,
      skipGroup = false,
      // antd-only props that must never reach the DOM
      prefixCls: _prefixCls,
      rootClassName: _rootClassName,
      onKeyPress: _onKeyPress,
      type: _type,
      ...rest
    },
    ref
  ) => {
    const group = React.useContext(CheckboxGroupContext)
    const inGroup = group !== null && !skipGroup
    const contextDisabled = React.useContext(DisabledContext)
    // antd's own merge, kept operator for operator: an explicit
    // `disabled={false}` still beats the ConfigProvider cascade.
    const disabled = (group?.disabled || disabledProp) ?? contextDisabled

    const buttonRef = React.useRef<HTMLButtonElement>(null)
    const generatedId = useStableId('checkbox')
    const resolvedId = id ?? generatedId

    const [uncontrolledChecked, setUncontrolledChecked] = React.useState(
      defaultChecked ?? false
    )
    const checked = inGroup
      ? group.value.includes(value as CheckboxValueType)
      : checkedProp ?? uncontrolledChecked

    // The registration antd's Group needs to know which of its values still
    // have a mounted child — the same two effects antd's Checkbox runs, so a
    // conditionally rendered option cannot linger in the emitted array.
    const registerValue = group?.registerValue
    const cancelValue = group?.cancelValue
    React.useEffect(() => {
      if (!inGroup) return undefined
      registerValue?.(value as CheckboxValueType)
      return () => cancelValue?.(value as CheckboxValueType)
    }, [inGroup, value, registerValue, cancelValue])

    React.useImperativeHandle(ref, () => ({
      focus: (options?: FocusOptions) => buttonRef.current?.focus(options),
      blur: () => buttonRef.current?.blur(),
      input: buttonRef.current as unknown as HTMLInputElement | null,
      nativeElement: buttonRef.current,
    }))

    const handleCheckedChange = () => {
      // NOT Radix's argument — antd toggles the real value. See the header.
      const nextChecked = !checked
      if (checkedProp === undefined && !inGroup) {
        setUncontrolledChecked(nextChecked)
      }
      const event = buildChangeEvent(
        {
          ...rest,
          value,
          disabled,
          id: resolvedId,
          name: inGroup ? group.name : name,
          required,
          title,
        },
        nextChecked,
        new MouseEvent('click')
      )
      onChange?.(event)
      if (inGroup) {
        group.toggleOption({
          label: children,
          value: value as CheckboxValueType,
        })
      }
    }

    return (
      <label
        data-slot="checkbox-wrapper"
        data-state={checked ? 'checked' : 'unchecked'}
        data-disabled={disabled || undefined}
        htmlFor={resolvedId}
        className={cn(
          // `items-center`, not `items-baseline`: the control is a 16px box
          // with no text baseline of its own, so a baseline alignment pins it
          // to the label's descender line and it rides visibly low. Verified
          // in Chrome against the antd rendering — see the port's report.
          'inline-flex cursor-pointer items-center gap-2 text-sm',
          // antd greys the LABEL and fades the box; the control fades itself
          // (`disabled:opacity-50`), so stacking an opacity here as well would
          // double-fade it to a quarter.
          disabled && 'cursor-not-allowed text-muted-foreground',
          className
        )}
        style={style}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <CheckboxPrimitive
          {...rest}
          ref={buttonRef}
          id={resolvedId}
          name={inGroup ? group.name : name}
          value={value as string | undefined}
          required={required}
          autoFocus={autoFocus}
          tabIndex={tabIndex}
          title={title}
          checked={indeterminate ? 'indeterminate' : checked}
          disabled={disabled}
          onCheckedChange={handleCheckedChange}
          onClick={onClick}
          onKeyDown={onKeyDown}
        />
        {children !== undefined && (
          <span data-slot="checkbox-label">{children}</span>
        )}
      </label>
    )
  }
)
CheckboxBase.displayName = 'Checkbox'

// forwardRef: antd's own Group forwards to the wrapping `<div>`, and a
// `Form.Item` hands its child a ref to scroll to on a failed validation.
const CheckboxGroupBase = React.forwardRef<HTMLDivElement, CheckboxGroupProps>(
  (
    {
      className,
      style,
      options = [],
      disabled,
      name,
      defaultValue,
      value: valueProp,
      onChange,
      children,
      // antd-only props that must never reach the DOM
      prefixCls: _prefixCls,
      rootClassName: _rootClassName,
      ...rest
    },
    ref
  ) => {
    const [uncontrolledValue, setUncontrolledValue] = React.useState<
      CheckboxValueType[]
    >(valueProp ?? defaultValue ?? [])
    const value = valueProp ?? uncontrolledValue
    const [registered, setRegistered] = React.useState<CheckboxValueType[]>([])

    const memoOptions = React.useMemo<CheckboxOptionType[]>(
      () =>
        options.map((option) =>
          typeof option === 'string' || typeof option === 'number'
            ? { label: option, value: option }
            : option
        ),
      [options]
    )

    const registerValue = React.useCallback(
      (val: CheckboxValueType) => setRegistered((prev) => [...prev, val]),
      []
    )
    const cancelValue = React.useCallback(
      (val: CheckboxValueType) =>
        setRegistered((prev) => prev.filter((v) => v !== val)),
      []
    )

    const toggleOption = (option: CheckboxOptionType) => {
      const index = value.indexOf(option.value)
      const next = [...value]
      if (index === -1) next.push(option.value)
      else next.splice(index, 1)
      if (valueProp === undefined) setUncontrolledValue(next)
      onChange?.(
        next
          .filter((val) => registered.includes(val))
          .sort(
            (a, b) =>
              memoOptions.findIndex((opt) => opt.value === a) -
              memoOptions.findIndex((opt) => opt.value === b)
          )
      )
    }

    const contextValue = React.useMemo(
      () => ({
        value,
        disabled,
        name,
        toggleOption,
        registerValue,
        cancelValue,
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [value, disabled, name, registered, memoOptions, onChange]
    )

    return (
      <div
        {...rest}
        ref={ref}
        data-slot="checkbox-group"
        // antd's group is an inline-flex row that wraps, with an 8px column
        // gap and no row gap. Reproduced rather than restyled: the 11 call
        // sites lay their own filter panels out around it.
        className={cn('inline-flex flex-wrap items-center gap-x-2', className)}
        style={style}
      >
        <CheckboxGroupContext.Provider value={contextValue}>
          {memoOptions.length
            ? memoOptions.map((option) => (
                <CheckboxBase
                  key={String(option.value)}
                  value={option.value}
                  disabled={'disabled' in option ? option.disabled : disabled}
                  style={option.style}
                  title={option.title}
                  id={option.id}
                  required={option.required}
                  onChange={option.onChange}
                >
                  {option.label}
                </CheckboxBase>
              ))
            : children}
        </CheckboxGroupContext.Provider>
      </div>
    )
  }
)
CheckboxGroupBase.displayName = 'Checkbox.Group'

// antd declares its Group as a generic function rather than a
// `ForwardRefExoticComponent`, so a call site writing
// `<Checkbox.Group<string> …>` — or simply relying on `value`/`onChange` to
// infer together — keeps inferring exactly as it does on the antd path.
const CheckboxGroup = CheckboxGroupBase as <
  T extends CheckboxValueType = CheckboxValueType
>(
  props: CheckboxGroupProps<T> & React.RefAttributes<HTMLDivElement>
) => React.ReactElement

export const Checkbox = Object.assign(CheckboxBase, { Group: CheckboxGroup })
